'use client';

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Script from 'next/script';

export type ReCAPTCHAHandle = {
  reset: () => void;
};

// Module-level: track script load and notify all waiting instances
let scriptReady = false;
const listeners = new Set<() => void>();

function notifyAll() {
  scriptReady = true;
  listeners.forEach((fn) => {
    fn();
  });
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function useRecaptcha() {
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHAHandle>(null);

  const onVerify = useCallback((token: string) => {
    setRecaptchaToken(token);
  }, []);

  const onExpire = useCallback(() => {
    setRecaptchaToken(null);
  }, []);

  const resetRecaptcha = useCallback(() => {
    setRecaptchaToken(null);
    recaptchaRef.current?.reset();
  }, []);

  return { recaptchaToken, recaptchaRef, onVerify, onExpire, resetRecaptcha };
}

type ReCAPTCHAProps = {
  onVerify: (token: string) => void;
  onExpire?: () => void;
};

const ReCAPTCHA = forwardRef<ReCAPTCHAHandle, ReCAPTCHAProps>(function ReCAPTCHA(
  { onVerify, onExpire },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);
  const callbacksRef = useRef({ onVerify, onExpire });
  callbacksRef.current = { onVerify, onExpire };

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current !== null && window.grecaptcha?.enterprise) {
        window.grecaptcha.enterprise.reset(widgetIdRef.current);
      }
    },
  }));

  const renderWidget = useCallback(() => {
    const enterprise = window.grecaptcha?.enterprise;
    if (enterprise && containerRef.current && widgetIdRef.current === null) {
      enterprise.ready(() => {
        if (containerRef.current && widgetIdRef.current === null && window.grecaptcha?.enterprise) {
          widgetIdRef.current = window.grecaptcha.enterprise.render(containerRef.current, {
            sitekey: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? '',
            callback: (token: string) => {
              callbacksRef.current.onVerify(token);
            },
            'expired-callback': () => callbacksRef.current.onExpire?.(),
          });
        }
      });
    }
  }, []);

  useEffect(() => {
    if (scriptReady) {
      renderWidget();
      return;
    }
    return subscribe(renderWidget);
  }, [renderWidget]);

  return (
    <>
      <Script
        id="recaptcha-enterprise"
        onReady={notifyAll}
        src="https://www.google.com/recaptcha/enterprise.js?render=explicit"
        strategy="lazyOnload"
      />
      <div ref={containerRef} />
    </>
  );
});

export default ReCAPTCHA;
